import {
  Box,
  Typography,
  Collapse,
  IconButton,
  Chip,
  Divider,
  Link,
  Button,
  MenuItem,
} from "@mui/material";
import {
  ChevronDown,
  ChevronUp,
  CircleDollarSign,
  Copy,
  X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import Select from "components/common/Select";
import useIsMobile from "components/hooks/useIsMobile";
import LinkRouter from "components/navigation/LinkRouter";
import { RematchSession } from "protogen/advisors_service_pb";
import { AccountStub } from "protogen/common_pb";
import { formatUSPhoneNumber } from "common/utils";
import { ContactDetail, BasicDetail } from "components/pipeline/Details";
import DateDisplay from "components/common/DateDisplay";
import { useSnackpack } from "components/context/SnackpackContextProvider";
import {
  useSetRematchScheduledSwitch,
  useClearRematchScheduledSwitch,
} from "services/admin";
import { useGetFamilyDetails } from "services/advisor";
import { useListFamilyAnnotations } from "services/engagement";
import AddAnnotation from "components/engagement/notes/AddAnnotation";
import AnnotationCard from "components/engagement/notes/AnnotationCard";
import Loading from "components/common/Loading";

const advisorLabel = (advisor: AccountStub): string =>
  advisor.displayName || `${advisor.firstName} ${advisor.lastName}`.trim();

const ScheduledSwitchSection = ({
  session,
  onUpdate,
}: {
  session: RematchSession;
  onUpdate?: () => void;
}) => {
  const { pushSnack } = useSnackpack();
  const [selectedAdvisorRef, setSelectedAdvisorRef] = useState<string>("");
  const [selectedEffectiveDate, setSelectedEffectiveDate] =
    useState<string>("");

  const { data: familyData, request: requestFamilyDetails } =
    useGetFamilyDetails();
  useEffect(() => {
    requestFamilyDetails({ familyRef: session.familyRef });
  }, [session.familyRef]);

  const { request: setScheduledSwitch, loading: setting } =
    useSetRematchScheduledSwitch(() => {
      pushSnack("Scheduled switch saved");
      setSelectedAdvisorRef("");
      setSelectedEffectiveDate("");
      onUpdate?.();
    });
  const { request: clearScheduledSwitch, loading: clearing } =
    useClearRematchScheduledSwitch(() => {
      pushSnack("Scheduled switch cleared");
      onUpdate?.();
    });

  const candidates: AccountStub[] = [];
  const seen = new Set<string>();
  const addCandidate = (advisor?: AccountStub) => {
    if (advisor && advisor.ref && !seen.has(advisor.ref)) {
      seen.add(advisor.ref);
      candidates.push(advisor);
    }
  };
  session.assignments?.forEach((assignment) =>
    addCandidate(assignment.advisor),
  );
  addCandidate(session.requestingAdvisor);

  const existing = session.scheduledSwitch;

  const formatCycleDate = (date: string): string =>
    new Date(date).toLocaleDateString("en-US", {
      day: "numeric",
      month: "long",
      year: "numeric",
      timeZone: "UTC",
    });
  const planUtilization = familyData?.planUtilization;
  const billingPeriodStart = planUtilization?.periodStartDate
    ? formatCycleDate(planUtilization.periodStartDate)
    : null;
  const billingPeriodEnd = planUtilization?.periodEndDate
    ? formatCycleDate(planUtilization.periodEndDate)
    : null;

  const isPastSwitch = (() => {
    if (!existing?.switchDate) return false;
    const [year, month, day] = existing.switchDate.split("-").map(Number);
    if (!year || !month || !day) return false;
    const switchDay = new Date(year, month - 1, day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return switchDay <= today;
  })();

  const handleSchedule = async () => {
    if (!selectedAdvisorRef || !selectedEffectiveDate) return;
    await setScheduledSwitch({
      rematchSessionRef: session.ref,
      advisorRef: selectedAdvisorRef,
      switchDate: selectedEffectiveDate,
    });
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "12px",
        borderTop: "1px solid #E2E3E4",
        padding: "20px 0",
      }}
    >
      <Typography variant="bodyHeavy">Scheduled Advisor Switch</Typography>
      {existing && existing.advisor ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "12px",
            background: "#EEF3FC",
            borderRadius: "8px",
            padding: "12px 16px",
          }}
        >
          <Typography variant="body">
            Switching to <b>{advisorLabel(existing.advisor)}</b> on{" "}
            <b>{existing.switchDate}</b>
          </Typography>
          {isPastSwitch ? (
            <Chip
              label="Completed"
              variant="filled"
              sx={{ background: "#ECF4EC", color: "#198282", height: "24px" }}
            />
          ) : (
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<X size={14} />}
              disabled={clearing}
              onClick={() =>
                clearScheduledSwitch({ rematchSessionRef: session.ref })
              }
              sx={{ "&:hover": { backgroundColor: "transparent" } }}
            >
              Clear
            </Button>
          )}
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "12px",
            maxWidth: "320px",
          }}
        >
          <Select
            label="Incoming advisor"
            variant="outlined"
            size="small"
            fullWidth
            value={selectedAdvisorRef}
            onChange={(e) => setSelectedAdvisorRef(e.target.value as string)}
            options={candidates.map((advisor) => ({
              label: advisorLabel(advisor),
              value: advisor.ref,
            }))}
          />
          <Select
            label="Switch date"
            variant="outlined"
            size="small"
            fullWidth
            value={selectedEffectiveDate}
            onChange={(e) => setSelectedEffectiveDate(e.target.value as string)}
          >
            {billingPeriodStart && (
              <MenuItem value="period-start">
                {billingPeriodStart} (this cycle)
              </MenuItem>
            )}
            {billingPeriodEnd && (
              <MenuItem value="period-end">
                {billingPeriodEnd} (next cycle)
              </MenuItem>
            )}
          </Select>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              disabled={
                !selectedAdvisorRef || !selectedEffectiveDate || setting
              }
              onClick={handleSchedule}
            >
              Schedule switch
            </Button>
          </Box>
          {candidates.length === 0 && (
            <Typography variant="body" color="text.secondary">
              No candidate advisors on this session yet.
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

const leftFlex = 3;
const rightFlex = 5;

const getStatusChip = (status: string) => {
  const getStatusConfig = (status: string) => {
    switch (status) {
      case "active":
        return { label: "Active", bgColor: "#EEF3FC", textColor: "#3061AF" };
      case "active_draft":
        return { label: "Draft", bgColor: "#FFF4E5", textColor: "#B95000" };
      case "client_email_sent":
        return {
          label: "Client email sent",
          bgColor: "#EEF3FC",
          textColor: "#3061AF",
        };
      case "client_followup_sent":
        return {
          label: "Client followup sent",
          bgColor: "#EEF3FC",
          textColor: "#3061AF",
        };
      case "advisor_selected":
        return {
          label: "Advisor selected",
          bgColor: "#EEF3FC",
          textColor: "#3061AF",
        };
      case "canceled":
        return {
          label: "Canceled",
          bgColor: "#F5F5F5",
          textColor: "#6B6E7B",
        };
      case "complete":
        return {
          label: "Complete",
          bgColor: "#ECF4EC",
          textColor: "#198282",
        };
      case "deleted":
        return {
          label: "Deleted",
          bgColor: "#F5F5F5",
          textColor: "#6B6E7B",
        };
      default:
        return { label: "Unknown", bgColor: "#F5F5F5", textColor: "#6B6E7B" };
    }
  };

  const config = getStatusConfig(status);
  return (
    <Chip
      label={config.label}
      variant="filled"
      sx={{
        background: config.bgColor,
        color: config.textColor,
        height: "24px",
      }}
    />
  );
};

const RematchDetails = ({ session }: { session: RematchSession }) => {
  const isMobile = useIsMobile();
  const { pushSnack } = useSnackpack();
  const family = session.family;
  const members = family?.familyMembers || [];

  return (
    <Box sx={{ padding: "0", flex: leftFlex }}>
      <Box
        display="flex"
        flexDirection={isMobile ? "column" : "row"}
        gap="10px"
      >
        {isMobile && (
          <Typography
            variant="body"
            sx={{ display: "flex", alignItems: "center", gap: "5px" }}
          >
            <DateDisplay
              date={new Date(Number(session.creationDateSec) * 1000)}
            />
          </Typography>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          {/* Family link */}
          {family && (
            <Typography variant="body">
              <LinkRouter
                sx={{ color: "#198282" }}
                to={`/families/${encodeURIComponent(family.ref)}`}
              >
                {family.name} Family
              </LinkRouter>
            </Typography>
          )}

          {/* Family Members */}
          {members.length > 0 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              <Typography variant="bodyHeavy">Family Members</Typography>
              {members.map((member) => (
                <Box
                  key={member.ref}
                  sx={{ display: "flex", flexDirection: "column", gap: "4px" }}
                >
                  <Typography variant="bodyHeavy" sx={{ fontSize: "14px" }}>
                    {member.displayName}
                  </Typography>
                  {member.primaryPhone && (
                    <ContactDetail
                      icon="Phone"
                      content={
                        <Link
                          href={`/inbox/phone/create?phone=${encodeURIComponent(
                            member.primaryPhone,
                          )}`}
                          sx={{ fontWeight: 500 }}
                        >
                          {formatUSPhoneNumber(member.primaryPhone)}
                        </Link>
                      }
                    />
                  )}
                  {member.primaryEmail && (
                    <ContactDetail
                      icon="Mail"
                      content={
                        <Link
                          href={`/inbox/email/create?email=${encodeURIComponent(
                            member.primaryEmail,
                          )}`}
                          sx={{ fontWeight: 500 }}
                        >
                          {member.primaryEmail}
                        </Link>
                      }
                    />
                  )}
                </Box>
              ))}
            </Box>
          )}

          {/* Address */}
          {family?.address &&
            (() => {
              try {
                const parsedAddress = JSON.parse(family.address);
                const formattedAddress = parsedAddress?.formattedAddress;
                if (!formattedAddress) return null;
                return (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "6px",
                    }}
                  >
                    <Typography variant="bodyHeavy">Address</Typography>
                    <ContactDetail
                      icon="MapPin"
                      content={
                        <Link
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                            formattedAddress,
                          )}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ fontWeight: 500 }}
                        >
                          {formattedAddress}
                        </Link>
                      }
                    />
                  </Box>
                );
              } catch {
                return null;
              }
            })()}

          {/* Family Status and Dates */}
          {family?.startDate && (
            <BasicDetail label="Start Date" content={family.startDate} />
          )}
          {family?.billingInfo?.product?.serviceHours && (
            <BasicDetail
              label="Package Hours"
              content={`${family.billingInfo.product.serviceHours} hours`}
            />
          )}
          {session.signupNotes && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "6px",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <Typography variant="bodyHeavy">Signup Notes</Typography>
                <IconButton
                  size="small"
                  onClick={async () => {
                    await navigator.clipboard.writeText(session.signupNotes);
                    pushSnack("Copied to clipboard!");
                  }}
                  sx={{ padding: "4px" }}
                >
                  <Copy size={14} />
                </IconButton>
              </Box>
              <Typography variant="body" color="text.secondary">
                {session.signupNotes}
              </Typography>
            </Box>
          )}
          {family?.endDate && (
            <BasicDetail label="End Date" content={family.endDate} />
          )}
        </Box>
      </Box>
    </Box>
  );
};

const RematchInfo = ({ session }: { session: RematchSession }) => {
  const isMobile = useIsMobile();
  const { pushSnack } = useSnackpack();

  const getLocationFromAddress = (address: string | undefined): string => {
    if (!address) return "N/A";
    try {
      const parsed = JSON.parse(address);
      const city = parsed?.city;
      const state = parsed?.state;
      const zipcode = parsed?.zipcode || parsed?.zip || parsed?.postalCode;
      if (city && state) {
        return `${city}, ${state}`;
      }
      if (zipcode) {
        return zipcode;
      }
      return "N/A";
    } catch {
      return "N/A";
    }
  };

  const handleCopyToClipboard = async () => {
    const family = session.family;
    const location = getLocationFromAddress(family?.address);
    const hours = family?.billingInfo?.product?.serviceHours || "N/A";

    const textToCopy = `Location: ${location}

Hours in current package: ${hours}

Current Tasks:
${session.currentTasks || "N/A"}

Household Description:
${session.householdDescription || "N/A"}

Additional Notes:
${session.additionalNotes || "N/A"}`;

    await navigator.clipboard.writeText(textToCopy);
    pushSnack("Copied to clipboard!");
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        borderTop: "1px solid #E2E3E4",
        padding: isMobile ? "20px 0 0" : "20px 0",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          gap: "24px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            gap: "16px",
          }}
        >
          {session.rematchReason && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              <Typography variant="bodyHeavy">Reason for Rematch</Typography>
              <Typography variant="body" color="text.secondary">
                {session.rematchReason}
              </Typography>
            </Box>
          )}
          {session.currentTasks && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              <Typography variant="bodyHeavy">Current Tasks</Typography>
              <Typography variant="body" color="text.secondary">
                {session.currentTasks}
              </Typography>
            </Box>
          )}
          {session.householdDescription && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              <Typography variant="bodyHeavy">Household Description</Typography>
              <Typography variant="body" color="text.secondary">
                {session.householdDescription}
              </Typography>
            </Box>
          )}
          {session.additionalNotes && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              <Typography variant="bodyHeavy">Additional Notes</Typography>
              <Typography variant="body" color="text.secondary">
                {session.additionalNotes}
              </Typography>
            </Box>
          )}
          {session.immediateRematch && (
            <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <Typography variant="bodyHeavy" color="error">
                Immediate Rematch Requested
              </Typography>
            </Box>
          )}
          <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <Typography variant="bodyHeavy">Client Communication</Typography>
            <Typography variant="body" color="text.secondary">
              {session.communicateWithClient
                ? session.clientCommunicationDate
                  ? `Planned for ${session.clientCommunicationDate}`
                  : "Advisor will communicate with client first"
                : "No - start on this immediately"}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box
        sx={{ display: "flex", justifyContent: "flex-end", marginTop: "16px" }}
      >
        <Button
          variant="outlined"
          startIcon={<Copy size={16} />}
          onClick={handleCopyToClipboard}
          sx={{ minWidth: "140px" }}
        >
          Copy to clipboard
        </Button>
      </Box>
    </Box>
  );
};

interface RematchCardProps {
  session: RematchSession;
  defaultOpen?: boolean;
  // When set, expanding hands off to the caller instead of opening in place.
  onExpand?: () => void;
  onUpdate?: () => void;
}

export default ({
  session,
  defaultOpen = false,
  onExpand,
  onUpdate,
}: RematchCardProps) => {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(defaultOpen);
  const loadedAnnotationsFor = useRef<string | null>(null);
  const {
    request: listFamilyAnnotations,
    data: annotationsResponse,
    loading: loadingAnnotations,
  } = useListFamilyAnnotations();

  const handleExpand = () => {
    if (onExpand) {
      onExpand();
    } else {
      setOpen(true);
    }
  };

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (open) {
      setOpen(false);
    } else {
      handleExpand();
    }
  };

  useEffect(() => {
    if (!open) return;
    if (loadedAnnotationsFor.current === session.familyRef) return;
    loadedAnnotationsFor.current = session.familyRef;
    listFamilyAnnotations({ familyRef: session.familyRef });
  }, [open, session.familyRef]);

  const family = session.family;
  if (!family) return null;

  const currentAdvisor = session.primaryAdvisor;
  const requestingAdvisor = session.requestingAdvisor;
  const nextBillingDate = family.nextBillingDate;
  const rematchAnnotations = (annotationsResponse?.annotations || []).filter(
    (annotation) => annotation.noteType === "rematch",
  );
  const rematchAnnotationCount = session.rematchNotesCount || 0;

  return (
    <Box
      display="flex"
      flexDirection="column"
      sx={{
        borderRadius: "8px",
        border: "1px solid #ECECEC",
        width: "100%",
        background: open ? "#FBFBFB" : "white",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "start" : "center",
          gap: "12px",
          cursor: !open ? "pointer" : undefined,
          padding: isMobile ? "12px 20px" : "16px 24px",
        }}
        onClick={() => !open && handleExpand()}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            width: "100%",
            justifyContent: isMobile ? "space-between" : "",
          }}
        >
          <Box
            sx={{
              flex: isMobile ? "" : leftFlex,
              display: "flex",
              flexDirection: "column",
              gap: "4px",
              overflow: "hidden",
            }}
          >
            <Typography variant={isMobile ? "h4" : "bodyHeavy"}>
              {family.name} Family
            </Typography>
            {requestingAdvisor && (
              <Typography variant="body" color="text.secondary">
                Requesting Advisor:{" "}
                {requestingAdvisor.displayName ||
                  `${requestingAdvisor.firstName} ${requestingAdvisor.lastName}`}
              </Typography>
            )}
            {currentAdvisor && (
              <Typography variant="body" color="text.secondary">
                Current Advisor:{" "}
                {currentAdvisor.displayName ||
                  `${currentAdvisor.firstName} ${currentAdvisor.lastName}`}
              </Typography>
            )}
            {nextBillingDate && (
              <Typography variant="body" color="text.secondary">
                Next Billing: {nextBillingDate}
              </Typography>
            )}
            {session.clientCredit && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  color: "success.main",
                }}
              >
                <CircleDollarSign size={18} />
                <Typography variant="body" color="success.main">
                  ${session.clientCredit.amountCents / 100} credit issued{" "}
                  {new Date(
                    Number(session.clientCredit.issuedSec) * 1000,
                  ).toLocaleDateString("en-US")}
                </Typography>
              </Box>
            )}
            {rematchAnnotationCount > 0 && (
              <Typography variant="body" sx={{ color: "warning.main" }}>
                #rematch notes: {rematchAnnotationCount}
              </Typography>
            )}
            {session.immediateRematch && (
              <Typography
                variant="body"
                color="error"
                sx={{ fontWeight: "bold" }}
              >
                Immediate Rematch
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              display: "flex",
              flex: isMobile ? "" : rightFlex,
              justifyContent: "space-between",
            }}
          >
            {!isMobile && getStatusChip(session.status)}
            <Box
              sx={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                alignItems: "center",
                gap: "8px",
              }}
            >
              {!isMobile && (
                <Typography variant="body">
                  <DateDisplay
                    date={new Date(Number(session.creationDateSec) * 1000)}
                  />
                </Typography>
              )}
              <IconButton
                aria-label="expand row"
                size="small"
                onClick={handleToggle}
              >
                {open ? <ChevronUp /> : <ChevronDown />}
              </IconButton>
            </Box>
          </Box>
        </Box>

        {isMobile && getStatusChip(session.status)}
      </Box>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Divider sx={{ borderColor: "#ECECEC" }} />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            background: "white",
            padding: "0px 24px 16px",
            borderBottomLeftRadius: "8px",
            borderBottomRightRadius: "8px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              gap: "24px",
              padding: "20px 0",
            }}
          >
            <RematchDetails session={session} />
            <Box sx={{ flex: rightFlex }} />
          </Box>
          <RematchInfo session={session} />
          <ScheduledSwitchSection session={session} onUpdate={onUpdate} />
          <Box sx={{ marginTop: "16px" }}>
            <Typography variant="bodyHeavy" sx={{ marginBottom: "8px" }}>
              Rematch Notes
            </Typography>
            <AddAnnotation
              familyRef={session.familyRef}
              signupSessionRef=""
              noteTypes={["rematch"]}
              includeFlags={false}
              placeholder="Add #rematch note"
              onUpdate={async () => {
                await listFamilyAnnotations({ familyRef: session.familyRef });
              }}
            />
            {loadingAnnotations && <Loading />}
            {!loadingAnnotations &&
              rematchAnnotations.map((annotation) => (
                <Box key={annotation.ref} sx={{ marginTop: "12px" }}>
                  <AnnotationCard annotation={annotation} />
                </Box>
              ))}
            {!loadingAnnotations && rematchAnnotations.length === 0 && (
              <Typography
                variant="body"
                color="text.secondary"
                sx={{ marginTop: "12px" }}
              >
                No rematch notes yet.
              </Typography>
            )}
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};
